/**
 * FC001P-50 — draws a recipe as a PNG card, entirely in the browser.
 *
 * Same approach as the asset creator's download (tradehub-asset-creator.js):
 * a canvas render, then canvas.toBlob, then an <a download> link. Nothing is
 * sent anywhere, so there is no print service or backend involved.
 *
 * Layout follows the existing recipe page (sc-recipe-main.liquid) as the
 * ticket asks: product photo, name, a bordered serves/difficulty/glass row,
 * an INGREDIENTS badge with quantity plus bold product name per line, then
 * numbered preparation steps. The card is A4 portrait wide and grows taller
 * rather than shrinking type when a recipe is long.
 */
(function () {
  'use strict';

  var WIDTH = 1240;
  var MIN_HEIGHT = 1754;
  var MARGIN = 90;
  /* Everything is painted on one tall scratch canvas first, then cropped to
     the height the content actually needed. */
  var SCRATCH_HEIGHT = 10000;
  var PHOTO_HEIGHT = 720;

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('image failed to load')); };
      img.src = url;
    });
  }

  function cssVar(name, fallback) {
    var value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
  }

  /* Canvas will not use a webfont that has not been fetched yet, so ask for
     each face we draw with first. A failure just means a fallback face. */
  function loadFonts(faces) {
    if (!document.fonts || typeof document.fonts.load !== 'function') return Promise.resolve();
    return Promise.all(faces.map(function (face) {
      return document.fonts.load(face).catch(function () {});
    }));
  }

  function wrapLines(ctx, text, maxWidth) {
    var out = [];
    String(text || '').split('\n').forEach(function (paragraph) {
      var current = '';
      paragraph.split(/\s+/).forEach(function (word) {
        if (!word) return;
        var candidate = current ? current + ' ' + word : word;
        if (ctx.measureText(candidate).width > maxWidth && current) {
          out.push(current);
          current = word;
        } else {
          current = candidate;
        }
      });
      out.push(current);
    });
    return out;
  }

  function fitText(ctx, text, maxWidth) {
    var out = String(text || '');
    if (ctx.measureText(out).width <= maxWidth) return out;
    while (out.length > 1 && ctx.measureText(out + '…').width > maxWidth) {
      out = out.slice(0, -1);
    }
    return out + '…';
  }

  function drawCover(ctx, img, x, y, w, h) {
    var scale = Math.max(w / img.naturalWidth, h / img.naturalHeight);
    var dw = img.naturalWidth * scale;
    var dh = img.naturalHeight * scale;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
    ctx.restore();
  }

  function sectionBadge(ctx, label, y, fonts) {
    ctx.font = '500 26px ' + fonts.mono;
    var text = String(label).toUpperCase();
    var padX = 26;
    var height = 56;
    var width = ctx.measureText(text).width + padX * 2;
    ctx.fillStyle = '#000';
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(MARGIN, y, width, height, 8);
    else ctx.rect(MARGIN, y, width, height);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, MARGIN + padX, y + height / 2 + 1);
    return y + height;
  }

  /**
   * card: {
   *   name, image, serves, difficulty, glass,
   *   ingredients: [{ quantity, unit, name }],
   *   method: [string],
   *   labels: { eyebrow, serves, difficulty, glass, ingredients, method, untitled }
   * }
   * Resolves { blob, width, height }.
   */
  function render(card) {
    var labels = card.labels || {};
    var fonts = {
      heading: cssVar('--font-th-heading-family', 'sans-serif'),
      body: cssVar('--font-th-body-family', 'sans-serif'),
      mono: cssVar('--font-th-mono-family', 'monospace')
    };

    var fontsReady = loadFonts([
      '900 80px ' + fonts.heading,
      '400 36px ' + fonts.body,
      '700 36px ' + fonts.body,
      '500 26px ' + fonts.mono
    ]);

    /* A missing or blocked photo just drops the banner; the card still
       exports. */
    var photoReady = card.image
      ? loadImage(card.image).catch(function () { return null; })
      : Promise.resolve(null);

    return Promise.all([fontsReady, photoReady]).then(function (results) {
      var photo = results[1];
      var scratch = document.createElement('canvas');
      scratch.width = WIDTH;
      scratch.height = SCRATCH_HEIGHT;
      var ctx = scratch.getContext('2d');
      var innerWidth = WIDTH - MARGIN * 2;
      var limit = SCRATCH_HEIGHT - MARGIN;

      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, WIDTH, SCRATCH_HEIGHT);
      ctx.textBaseline = 'top';
      ctx.textAlign = 'left';

      var y = MARGIN;
      if (photo) {
        drawCover(ctx, photo, 0, 0, WIDTH, PHOTO_HEIGHT);
        y = PHOTO_HEIGHT + 70;
      }

      ctx.fillStyle = '#666';
      ctx.font = '500 26px ' + fonts.mono;
      ctx.fillText(String(labels.eyebrow || '').toUpperCase(), MARGIN, y);
      y += 52;

      var title = String(card.name || labels.untitled || '').toUpperCase();
      var size = 150;
      var lines;
      for (;;) {
        ctx.font = '900 ' + size + 'px ' + fonts.heading;
        lines = wrapLines(ctx, title, innerWidth);
        var widest = lines.reduce(function (max, line) {
          return Math.max(max, ctx.measureText(line).width);
        }, 0);
        if ((lines.length <= 3 && widest <= innerWidth) || size <= 60) break;
        size -= 6;
      }
      ctx.fillStyle = '#000';
      lines.forEach(function (line) {
        ctx.fillText(line, MARGIN, y);
        y += size * 1.02;
      });
      y += 28;
      ctx.fillRect(MARGIN, y, innerWidth, 4);
      y += 4 + 56;

      /* Serves / difficulty / glass, as a bordered three-cell table. */
      var infoHeight = 140;
      var cellWidth = innerWidth / 3;
      var cells = [
        [labels.serves, card.serves],
        [labels.difficulty, card.difficulty],
        [labels.glass, card.glass]
      ];
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#000';
      ctx.setLineDash([]);
      ctx.strokeRect(MARGIN, y, innerWidth, infoHeight);
      cells.forEach(function (cell, index) {
        var x = MARGIN + cellWidth * index;
        if (index) {
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x, y + infoHeight);
          ctx.stroke();
        }
        ctx.fillStyle = '#666';
        ctx.font = '500 22px ' + fonts.mono;
        ctx.fillText(String(cell[0] || '').toUpperCase(), x + 30, y + 28);
        ctx.fillStyle = '#000';
        ctx.font = '700 44px ' + fonts.body;
        ctx.fillText(fitText(ctx, cell[1], cellWidth - 60), x + 30, y + 74);
      });
      y += infoHeight + 80;

      var ingredients = card.ingredients || [];
      if (ingredients.length) {
        y = sectionBadge(ctx, labels.ingredients, y, fonts) + 30;
        var amountWidth = 240;
        ingredients.forEach(function (ingredient) {
          if (y > limit) return;
          ctx.font = '700 38px ' + fonts.body;
          var nameLines = wrapLines(ctx, ingredient.name, innerWidth - amountWidth);
          var rowHeight = nameLines.length * 48 + 28;

          ctx.fillStyle = '#000';
          ctx.font = '400 38px ' + fonts.body;
          ctx.fillText(ingredient.quantity + ingredient.unit, MARGIN, y + 14);
          ctx.font = '700 38px ' + fonts.body;
          nameLines.forEach(function (line, i) {
            ctx.fillText(line, MARGIN + amountWidth, y + 14 + i * 48);
          });

          y += rowHeight;
          ctx.strokeStyle = '#cfcfcf';
          ctx.lineWidth = 2;
          ctx.setLineDash([8, 8]);
          ctx.beginPath();
          ctx.moveTo(MARGIN, y);
          ctx.lineTo(MARGIN + innerWidth, y);
          ctx.stroke();
          ctx.setLineDash([]);
        });
        y += 50;
      }

      var method = card.method || [];
      if (method.length) {
        y = sectionBadge(ctx, labels.method, y, fonts) + 30;
        method.forEach(function (step, index) {
          if (y > limit) return;
          ctx.font = '400 38px ' + fonts.body;
          var stepLines = wrapLines(ctx, step, innerWidth - 90);
          var rowHeight = stepLines.length * 48 + 34;

          ctx.strokeStyle = '#000';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.arc(MARGIN + 25, y + 38, 25, 0, Math.PI * 2);
          ctx.stroke();
          ctx.fillStyle = '#000';
          ctx.font = '500 26px ' + fonts.mono;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(String(index + 1), MARGIN + 25, y + 39);
          ctx.textAlign = 'left';
          ctx.textBaseline = 'top';

          ctx.font = '400 38px ' + fonts.body;
          stepLines.forEach(function (line, i) {
            ctx.fillText(line, MARGIN + 90, y + 14 + i * 48);
          });

          y += rowHeight;
        });
      }

      var height = Math.max(MIN_HEIGHT, Math.ceil(y + MARGIN));
      var out = document.createElement('canvas');
      out.width = WIDTH;
      out.height = height;
      var outCtx = out.getContext('2d');
      outCtx.drawImage(scratch, 0, 0, WIDTH, height, 0, 0, WIDTH, height);

      return new Promise(function (resolve, reject) {
        out.toBlob(function (blob) {
          if (blob) resolve({ blob: blob, width: WIDTH, height: height });
          else reject(new Error('canvas.toBlob returned nothing'));
        }, 'image/png');
      });
    });
  }

  window.ThRecipeCard = { render: render };
})();
